/**
 * CaseUpdateComposer - Teams-style update composer for case items.
 * Supports: optional title, message body, attachments, links, tags (Report, Contact, Customer, Project).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PaperAirplaneIcon, PlusIcon } from "@heroicons/react/24/outline";
import { DocumentUploadSectionV4 } from "../Media/DocumentUploadSectionV4";
import { DocumentViewer } from "../Media/documentViewer";
import { MentionTextEditor, extractMentionedUsers, extractModuleTags } from "../../utils/MentionTextEditor";
import { TWclassNames } from "../../utils/Div";
import { priorityOptions } from "../quality/deviations/utils";

export function CaseUpdateComposer({
  caseId,
  onSubmit,
  disabled = false,
  onDocumentRefresh,
  compact = false,
  customer,
  /** When true, only users with case role (can read/edit cases) can be tagged */
  restrictMentionsToCaseUsers = true,
}) {
  const { t } = useTranslation();
  const [showTitle, setShowTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState(""); // HTML from MentionTextEditor
  const [documents, setDocuments] = useState([]);
  const [tags, setTags] = useState([]); // From slash / picks via onTagSelect
  const [priority, setPriority] = useState(null); // 1=Low, 2=Medium, 3=High, 4=Critical
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasContent =
    (message?.trim() && message !== "<p><br></p>") ||
    documents?.length > 0 ||
    tags?.length > 0 ||
    title?.trim();

  const handleUploadCallback = (uploadedDocs) => {
    const merged = [...documents, ...uploadedDocs].filter(
      (v, i, a) => a.findIndex((d) => d.id === v.id) === i
    );
    setDocuments(merged);
  };

  const handleSubmit = async () => {
    if (!hasContent || !onSubmit || !caseId || disabled || isSubmitting) return;

    let description = message?.trim() || "";
    if (description === "<p><br></p>") description = "";

    const mentionedUsers = extractMentionedUsers(message || "");
    const userTags = mentionedUsers.map((u) => ({
      module: "user",
      module_param: u.id,
    }));
    const slashTags = extractModuleTags(message || "");
    const tagKeys = new Set(tags.map((t) => `${t.module}:${t.module_param}`));
    const mergedSlashTags = slashTags.filter((t) => !tagKeys.has(`${t.module}:${t.module_param}`));
    const allTags = [...tags, ...mergedSlashTags, ...userTags];
    const notifyEmployees = mentionedUsers.length > 0 ? mentionedUsers.map((u) => ({ id: u.id })) : undefined;

    // API expects tags as [{ module, module_param }] - ensure valid format, filter invalid
    const tagsForApi = allTags.length > 0
      ? allTags
          .map((t) => ({
            module: String(t.module || ""),
            module_param: t.module_param != null ? String(t.module_param) : "",
          }))
          .filter((t) => t.module && t.module_param)
      : undefined;

    const payload = {
      case_id: caseId,
      title: title?.trim(),
      description: description || " ",
      action: "ADDED",
      module: "description",
      documents: documents.map((d) => ({ id: d.id })),
      tags: tagsForApi,
      notifyEmployees: notifyEmployees,
      priority: priority != null ? String(priority) : undefined,
    };

    setIsSubmitting(true);
    try {
      await onSubmit(payload);
      setMessage("");
      setTitle("");
      setDocuments([]);
      setTags([]);
      setPriority(null);
      setShowTitle(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={TWclassNames(
        "rounded-xl border border-gray-200 bg-white shadow-sm",
        compact && "text-sm"
      )}
    >
      {/* Optional title row - Teams "Add a subject" style */}
      {showTitle && (
        <div className="border-b border-gray-100 px-3 py-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("Title (optional)")}
            className="w-full rounded-lg border-0 bg-transparent py-1 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
        </div>
      )}

      {/* Main message area - rich text with @mentions and / for tags */}
      <div className="px-3 py-2">
        <MentionTextEditor
          value={message}
          onChange={setMessage}
          placeholder={t("Write an update...")}
          minHeight={compact ? 80 : 100}
          maxHeight={compact ? 120 : 180}
          disabled={disabled}
          userFilters={restrictMentionsToCaseUsers ? [{ name: "role", value: "case" }] : []}
          enableSlashCommands={true}
          onTagSelect={(tag) => setTags((prev) => [...prev, tag])}
          customer={customer}
        />
      </div>

      {/* Attachments preview */}
      {documents?.length > 0 && (
        <div className="border-t border-gray-100 px-3 py-2">
          <DocumentViewer
            size="small"
            pathType="url"
            documents={documents}
            onChange={setDocuments}
          />
        </div>
      )}

      {/* Toolbar - use @ for mentions, / for tags */}
      <div className="flex items-center justify-between border-t border-gray-200 px-2 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowTitle(!showTitle)}
            className={TWclassNames(
              "flex items-center gap-1 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700",
              showTitle && "bg-gray-100 text-blue-600"
            )}
            title={t("Add title")}
          >
            <PlusIcon className="h-5 w-5" />
            <span className="text-xs">{t("Title")}</span>
          </button>

          <DocumentUploadSectionV4
            iconOnly
            compact
            module="CASE"
            module_param={caseId}
            moduleParam={caseId}
            parentCallback={handleUploadCallback}
          />

          <select
            value={priority ?? ""}
            onChange={(e) => setPriority(e.target.value !== "" ? e.target.value : null)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            title={t("Prioritering")}
          >
            <option value="">{t("Prioritering")}</option>
            {priorityOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.label)}
              </option>
            ))}
          </select>
        </div>

        {/* Send */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!hasContent || disabled || isSubmitting}
          className={TWclassNames(
            "flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            hasContent && !disabled && !isSubmitting
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "cursor-not-allowed bg-gray-100 text-gray-400"
          )}
        >
          <PaperAirplaneIcon className="h-5 w-5" />
          {t("Send")}
        </button>
      </div>
    </div>
  );
}

export default CaseUpdateComposer;
